import React, { Component } from 'react';
import { graphql, compose } from 'react-apollo';
import PropTypes from 'prop-types';
import moment from 'moment';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import { FaSave, FaEdit } from 'react-icons/fa';
import { MdCheckCircle, MdRemoveCircle } from 'react-icons/md';

import GridItem from '../../../components/Grid/GridItem';
import GridContainer from '../../../components/Grid/GridContainer';
import Card from '../../../components/Card/Card';
import CardHeader from '../../../components/Card/CardHeader';
import CardBody from '../../../components/Card/CardBody';
import Button from '../../../components/CustomButtons/Button';
import { Dialog } from '../../../components';
import { getLeaderboardByAdmin } from './queries';
import { updateLeaderboard, activeLeaderboard } from './mutations';
import styles from './styles';
import { colors } from '../../../constants';

/**
 * datetime-local → Eastern wall-clock string for API.
 * Server converts America/New_York → UTC (with DST).
 */
const toServerDateTime = (value) => {
  if (!value) return '';
  const parsed = moment(value, 'YYYY-MM-DDTHH:mm', true);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY HH:mm:ss') : '';
};

/** API Eastern datetime → datetime-local */
const toInputDateTime = (value) => {
  if (!value) return '';
  const parsed = moment(value, ['MM/DD/YYYY HH:mm:ss', 'MM/DD/YYYY HH:mm', 'MM/DD/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
};

class DetailLeaderboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      edit: false,
      name: '',
      periodFrom: '',
      periodTo: '',
      active: 0,
      open: false,
      title: '',
      content: '',
      confirmActivateOpen: false,
      saving: false,
      activating: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { detailLeaderboard } = this.props;
    if (
      detailLeaderboard
      && detailLeaderboard !== prevProps.detailLeaderboard
      && !this.state.edit
    ) {
      this.loadFromDetail(detailLeaderboard);
    }
  }

  loadFromDetail = (detail) => {
    this.setState({
      name: detail.name || '',
      periodFrom: toInputDateTime(detail.periodFrom),
      periodTo: toInputDateTime(detail.periodTo),
      active: detail.active,
    });
  };

  onEdit = () => {
    const { edit } = this.state;
    if (!edit && this.props.detailLeaderboard) {
      this.loadFromDetail(this.props.detailLeaderboard);
    }
    this.setState({ edit: !edit });
  };

  handleChange = (event) => {
    this.setState({ [event.target.name]: event.target.value });
  };

  onSave = async () => {
    const { name, periodFrom, periodTo } = this.state;
    const { updateLeaderboard: updateMutation, match } = this.props;
    const id = Number(match.params.id);

    if (!name || !periodFrom || !periodTo) {
      this.setState({
        open: true,
        title: 'Update Leaderboard',
        content: 'Name, Period From and Period To are required.',
      });
      return;
    }

    this.setState({ saving: true });
    try {
      await updateMutation({
        variables: {
          input: {
            id,
            name,
            periodFrom: toServerDateTime(periodFrom),
            periodTo: toServerDateTime(periodTo),
          },
        },
        refetchQueries: [{ query: getLeaderboardByAdmin, variables: { id } }],
      });
      this.setState({
        saving: false,
        edit: false,
        open: true,
        title: 'Update Leaderboard',
        content: 'Completed',
      });
    } catch (error) {
      const message = error && error.graphQLErrors && error.graphQLErrors[0]
        ? error.graphQLErrors[0].message
        : 'Update failed';
      this.setState({
        saving: false,
        open: true,
        title: 'Update Leaderboard',
        content: message,
      });
    }
  };

  onActivate = async () => {
    const { activeLeaderboard: activateMutation, match } = this.props;
    const id = Number(match.params.id);

    this.setState({ activating: true });
    try {
      await activateMutation({
        variables: { input: { id } },
        refetchQueries: [{ query: getLeaderboardByAdmin, variables: { id } }],
      });
      this.setState({
        activating: false,
        confirmActivateOpen: false,
        active: 1,
        open: true,
        title: 'Activate Leaderboard',
        content: 'Leaderboard activated successfully.',
      });
    } catch (error) {
      const message = error && error.graphQLErrors && error.graphQLErrors[0]
        ? error.graphQLErrors[0].message
        : 'Activation failed';
      this.setState({
        activating: false,
        confirmActivateOpen: false,
        open: true,
        title: 'Activate Leaderboard',
        content: message,
      });
    }
  };

  render() {
    const { classes, loading } = this.props;
    const {
      edit, name, periodFrom, periodTo, active,
      open, title, content, confirmActivateOpen, saving, activating,
    } = this.state;

    if (loading) return null;

    return (
      <GridContainer justify="center">
        <Dialog
          title={title}
          content={content}
          open={open}
          onOk={() => this.setState({ open: false })}
        />
        <Dialog
          title="Activate Leaderboard"
          content="This will deactivate the current active leaderboard and activate this one. Continue?"
          open={confirmActivateOpen}
          onOk={this.onActivate}
          onCancel={() => this.setState({ confirmActivateOpen: false })}
          cancelText="Cancel"
        />
        <GridItem xs={10} sm={8} md={6}>
          <Card>
            <CardHeader>{edit ? 'Edit Leaderboard' : 'Detail Leaderboard'}</CardHeader>
            <CardBody>
              <Typography className={classes.activeLabel}>
                Active:{' '}
                {active === 1
                  ? <MdCheckCircle color={colors.dark} size={16} />
                  : <MdRemoveCircle color={colors.red} size={16} />}
              </Typography>
              <TextField
                label="Name"
                name="name"
                value={name}
                onChange={this.handleChange}
                fullWidth
                className={classes.field}
                disabled={!edit}
              />
              <TextField
                label="Period From (Eastern Time)"
                name="periodFrom"
                type="datetime-local"
                value={periodFrom}
                onChange={this.handleChange}
                fullWidth
                className={classes.field}
                disabled={!edit}
                helperText="America/New_York — enter Miami/OH wall-clock time (DST applied on server)"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Period To (Eastern Time)"
                name="periodTo"
                type="datetime-local"
                value={periodTo}
                onChange={this.handleChange}
                fullWidth
                className={classes.field}
                disabled={!edit}
                InputLabelProps={{ shrink: true }}
              />
              <div className={classes.actions}>
                {!edit ? (
                  <Button variant="contained" color="primary" className={classes.button} onClick={this.onEdit}>
                    <Typography component="p" className={classes.textEdit}>Edit</Typography>
                    <FaEdit color="#ffffff" size={16} />
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    onClick={this.onSave}
                    disabled={saving}
                  >
                    <Typography component="p" className={classes.textEdit}>
                      {saving ? 'Saving...' : 'Save'}
                    </Typography>
                    <FaSave color="#ffffff" size={16} />
                  </Button>
                )}
                {active !== 1 && (
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    onClick={() => this.setState({ confirmActivateOpen: true })}
                    disabled={activating}
                  >
                    <Typography component="p" className={classes.textEdit}>Activate</Typography>
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

DetailLeaderboard.propTypes = {
  classes: PropTypes.object,
  loading: PropTypes.bool,
  detailLeaderboard: PropTypes.object,
  updateLeaderboard: PropTypes.func,
  activeLeaderboard: PropTypes.func,
  match: PropTypes.object,
};

DetailLeaderboard.defaultProps = {
  classes: {},
  loading: false,
  detailLeaderboard: {},
  updateLeaderboard: () => {},
  activeLeaderboard: () => {},
  match: {},
};

const DetailQuery = graphql(getLeaderboardByAdmin, {
  options: (props) => ({
    variables: { id: Number(props.match.params.id) },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data, ownProps }) => {
    const detail = data.getLeaderboardByAdmin;
    return {
      loading: data.loading,
      detailLeaderboard: detail,
    };
  },
});

const Update = graphql(updateLeaderboard, { name: 'updateLeaderboard' });
const Activate = graphql(activeLeaderboard, { name: 'activeLeaderboard' });

export default withStyles(styles)(compose(DetailQuery, Update, Activate)(DetailLeaderboard));
